import { formatFullDate } from "@/lib/format";
import { editionLabel } from "@/lib/license";
import type { AdminApplication } from "@/pages/admin/hooks/useAdminData";

interface BatchConfirmModalProps {
  open: boolean;
  action: "approve" | "reject";
  applications: AdminApplication[];
  months: number;
  note: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function BatchConfirmModal({
  open,
  action,
  applications,
  months,
  note,
  busy,
  onConfirm,
  onCancel,
}: BatchConfirmModalProps) {
  if (!open) return null;

  const isApprove = action === "approve";
  const emails = applications.filter((item) => item.email);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8">
      <button
        type="button"
        aria-label="关闭确认弹窗"
        onClick={() => {
          if (!busy) onCancel();
        }}
        className="absolute inset-0 cursor-pointer bg-foreground-950/50"
      />

      <div
        role="dialog"
        aria-modal="true"
        className="lh-fade-up relative z-10 flex max-h-full w-full max-w-xl flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50"
      >
        <div className="flex items-start gap-3 border-b border-background-200 px-5 py-4">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${
              isApprove ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-700"
            }`}
          >
            <i className={`${isApprove ? "ri-check-double-line" : "ri-close-circle-line"} text-xl`} />
          </span>
          <div className="flex-1">
            <h2 className="font-heading text-base font-semibold text-foreground-950">
              {isApprove ? `确认批量通过 ${applications.length} 条申请？` : `确认批量驳回 ${applications.length} 条申请？`}
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-foreground-600">
              {isApprove
                ? "将通过以下申请并自动签发许可证"
                : "将驳回以下申请"}
              {isApprove && emails.length > 0 && `，并向 ${emails.length} 位申请人发送通知邮件`}
              。此操作执行后不可撤销，请再次核对清单。
            </p>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            aria-label="关闭"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="border-b border-background-200 bg-background-100 px-5 py-3">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
            {isApprove && (
              <span className="flex items-center gap-1.5 text-foreground-700">
                <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-700">
                  <i className="ri-time-line" />
                </span>
                授权有效期：<strong className="text-foreground-950">{months} 个月</strong>
              </span>
            )}
            <span className="flex items-center gap-1.5 text-foreground-700">
              <span className="flex h-3.5 w-3.5 items-center justify-center text-foreground-500">
                <i className="ri-chat-3-line" />
              </span>
              {isApprove ? "审批备注" : "驳回原因"}：{note.trim() || "（未填写）"}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <ul className="space-y-2.5">
            {applications.map((item, index) => (
              <li
                key={item.id}
                className="flex items-start gap-3 rounded-md border border-background-200 bg-background-50 px-3.5 py-3"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-background-100 text-[11px] font-medium text-foreground-600">
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-sm font-semibold text-foreground-950">{item.product_name}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-500">
                    <span className="flex items-center gap-1">
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-user-line" />
                      </span>
                      {item.applicant_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-mail-line" />
                      </span>
                      {item.email || "—"}
                    </span>
                    <span>{editionLabel(item.edition)}</span>
                    <span>{item.seat_count} 席位</span>
                    <span>{formatFullDate(item.created_at)} 提交</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-background-200 bg-background-50 px-5 py-4">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            返回检查
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-5 py-2 text-sm font-medium text-background-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              isApprove ? "bg-primary-500 hover:bg-primary-600" : "bg-accent-600 hover:bg-accent-700"
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={isApprove ? "ri-shield-check-line" : "ri-close-circle-line"} />
            </span>
            {busy ? "处理中…" : isApprove ? `确认批量通过（${applications.length}）` : `确认批量驳回（${applications.length}）`}
          </button>
        </div>
      </div>
    </div>
  );
}